<template>
  <!-- Card Number -->
  <div class="my-4">
    <span class="style-field-label">Card Number</span>
    <div style="position: relative">
      <div
        id="card-number-element"
        :class="[
          'style-input-field',
          { 'style-error-input-field': event.cardNumberElement?.error },
        ]"
      ></div>
      <card-brand-icon
        :cardBrand="event.cardNumberElement?.brand"
        class="style-card-brand-icon"
      />
    </div>
    <span v-if="event.cardNumberElement?.error" class="style-error-message">
      {{ event.cardNumberElement.error.message }}
    </span>
  </div>

  <!-- Card Expiry & CVC -->
  <div class="d-flex flex-row">
    <!-- Card Expiry -->
    <div style="flex: 1">
      <span class="style-field-label">Expiration</span>
      <div
        id="card-expiry-element"
        :class="[
          'style-input-field',
          { 'style-error-input-field': event.cardExpiryElement?.error },
        ]"
      ></div>
      <span v-if="event.cardExpiryElement?.error" class="style-error-message">
        {{ event.cardExpiryElement.error.message }}
      </span>
    </div>

    <div style="width: 25px">&nbsp;</div>

    <!-- CVC -->
    <div style="flex: 1">
      <span class="style-field-label">CVC</span>
      <div style="position: relative">
        <div
          id="card-cvc-element"
          :class="[
            'style-input-field',
            { 'style-error-input-field': event.cardCvcElement?.error },
          ]"
        ></div>
        <img src="@/assets/icons/cards/cvv-icon.png" class="style-cvc-icon" />
      </div>
      <span v-if="event.cardCvcElement?.error" class="style-error-message">
        {{ event.cardCvcElement.error.message }}
      </span>
    </div>
  </div>

  <!-- Select Country -->
  <div class="my-4">
    <span class="style-field-label">Country</span>
    <select
      id="billing-country"
      class="style-country-input w-100"
      v-model="selectedCountry"
    >
      <option
        v-for="country in stripeCountryList"
        :key="country.name"
        :value="country.value"
      >
        {{ country.name }}
      </option>
    </select>
  </div>

  <span class="style-terms-and-condition"
    >By providing your card information, you allow {{ websiteURL }} to change
    your card for future payments in accordance with their terms.</span
  >
</template>

<script>
import { stripeCountries } from "@/common/constant/Countries";
import CardBrandIcon from "@/components/onlinePayment/CardBrandIcon";

export default {
  name: "StripePaymentForm",
  emits: ["update:billingCountry", "completed"],
  components: { CardBrandIcon },
  props: {
    elements: {
      required: true,
    },
    billingCountry: {
      required: true,
    },
  },
  data() {
    return {
      cardNumberElement: null,
      cardExpiryElement: null,
      cardCvcElement: null,
      event: {
        cardNumberElement: null,
        cardExpiryElement: null,
        cardCvcElement: null,
      },
    };
  },
  computed: {
    stripeCountryList() {
      return stripeCountries;
    },
    selectedCountry: {
      set(value) {
        this.$emit("update:billingCountry", value);
      },
      get() {
        return this.billingCountry;
      },
    },
    isPaymentFormCompleted() {
      return (
        this.event.cardNumberElement?.complete &&
        this.event.cardExpiryElement?.complete &&
        this.event.cardCvcElement?.complete &&
        this.selectedCountry !== ""
      );
    },
    websiteURL() {
      return window.location.href.split("//")[1].split("/")[0];
    },
  },
  methods: {
    setupPaymentForm() {
      const style = {
        base: {},
      };
      // Create card number element
      this.cardNumberElement = this.elements.create("cardNumber", {
        style: style,
      });
      this.cardNumberElement.mount("#card-number-element");
      // Add event listener
      this.cardNumberElement.addEventListener("change", (event) =>
        this.handleCardNumberElementChangeEvent(event)
      );

      // Create card expiry element
      this.cardExpiryElement = this.elements.create("cardExpiry", {
        style: style,
      });
      this.cardExpiryElement.mount("#card-expiry-element");
      // Add event listener
      this.cardExpiryElement.addEventListener("change", (event) =>
        this.handleCardExpiryElementChangeEvent(event)
      );

      // // Create card cvc element
      this.cardCvcElement = this.elements.create("cardCvc", {
        style: style,
      });
      this.cardCvcElement.mount("#card-cvc-element");
      // Add event listener
      this.cardCvcElement.addEventListener("change", (event) =>
        this.handleCardCvcElementChangeEvent(event)
      );
    },
    handleCardNumberElementChangeEvent(event) {
      this.event.cardNumberElement = event;
    },
    handleCardExpiryElementChangeEvent(event) {
      this.event.cardExpiryElement = event;
    },
    handleCardCvcElementChangeEvent(event) {
      this.event.cardCvcElement = event;
    },
  },
  watch: {
    elements(value) {
      if (value !== null) {
        this.setupPaymentForm();
      }
    },
    isPaymentFormCompleted(value) {
      this.$emit("completed", value);
    },
  },
  created() {
    // Set default selected country
    this.selectedCountry = "";
  },
};
</script>

<style lang="scss" scoped>
.style-input-field {
  height: 50px;
  border: 1px solid #adadad;
  padding: 15px 10px;
  border-radius: 10px;
  flex: 1;
}

.style-country-input {
  height: 50px;
  border: 1px solid #adadad;
  padding: 0px 10px;
  display: flex;
  align-items: center;
  border-radius: 10px;
  flex: 1;
}

.style-error-input-field {
  border: 1px solid $error-color !important;
}

.style-error-message {
  color: $error-color;
  display: flex;
  flex: start;
  text-align: start;
}

.style-field-label {
  display: flex;
  flex: start;
}

.style-terms-and-condition {
  font-size: 12px;
  display: flex;
  flex: start;
  text-align: start;
  color: $text-light-color;
}

.style-cvc-icon {
  position: absolute;
  right: 5px;
  top: 50%;
  width: 35px;
  height: 35px;
  transform: translate(0%, -50%);
}

.style-card-brand-icon {
  position: absolute;
  right: 5px;
  top: 50%;
  width: 40px;
  height: 40px;
  transform: translate(0%, -50%);
}
</style>
